// Utility functions
import { logger } from "@utilities/logger";
import type { ContentSlice, STTResponse } from "./types";
import type { ContentItem } from "./types";

// Preprocesses transcript text to convert bracketed phrases into single tokens
const preprocessTranscriptForBrackets = (transcript: string): string => {
  // Replace bracketed phrases with single tokens using a special delimiter
  return transcript.replace(/\[([^\]]*)\]/g, (match, content) => {
    // Convert spaces to underscores to make it a single "word"
    return `[${content.replace(/\s+/g, "_")}]`;
  });
};

// Counts the number of words in a transcript, excluding words surrounded by square brackets.
const countWordsInTranscript = (transcript: string): number => {
  // Remove bracketed phrases first (e.g., [said loudly], [whispers])
  const textWithoutBrackets = transcript.replace(/\[[^\]]*\]/g, "");

  return textWithoutBrackets
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0).length;
};

// Utility function to parse display text into words while preserving whitespace
export const parseDisplayText = (displayText: string): string[] => {
  const result: string[] = [];
  let currentWord = "";

  for (let i = 0; i < displayText.length; i++) {
    const char = displayText[i];
    if (/\s/.test(char)) {
      if (currentWord) {
        result.push(currentWord);
        currentWord = "";
      }
      // Add whitespace as separate elements
      let whitespace = char;
      while (i + 1 < displayText.length && /\s/.test(displayText[i + 1])) {
        i++;
        whitespace += displayText[i];
      }
      result.push(whitespace);
    } else {
      currentWord += char;
    }
  }

  if (currentWord) {
    result.push(currentWord);
  }

  return result;
};

// Combined utility that parses STT response and creates content slices in a single pass
export const createContentSlices = (
  sttResponse: null | string | undefined,
  content: ContentItem[] | null | undefined,
): ContentSlice[] => {
  // Early return if no content
  if (!content || content.length === 0) {
    return content?.map(item => ({ ...item, sttSlice: null })) || [];
  }

  // Parse STT response
  let sttData: STTResponse | null = null;
  if (sttResponse) {
    try {
      sttData = JSON.parse(sttResponse);
    } catch (error) {
      logger.error(error instanceof Error ? error : null, "Failed to parse STT response");
    }
  }

  // Early return if no valid STT data
  if (!sttData?.words || sttData.words.length === 0) {
    return content.map(item => ({ ...item, sttSlice: null }));
  }

  // Single pass: extract words, times, and count transcript words
  const words: string[] = [];
  const startTimes: number[] = [];
  const endTimes: number[] = [];
  const transcriptWordCounts: number[] = [];
  let totalTranscriptWords = 0;

  // Process STT data in single iteration
  for (const word of sttData.words) {
    words.push(word.text);
    startTimes.push(word.start);
    endTimes.push(word.end);
  }

  // Process content items and count words
  for (const item of content) {
    const wordCount = item.transcript ? countWordsInTranscript(item.transcript) : 0;
    transcriptWordCounts.push(wordCount);
    totalTranscriptWords += wordCount;
  }

  // If no transcript words, return items with null slices
  if (totalTranscriptWords === 0) {
    return content.map(item => ({ ...item, sttSlice: null }));
  }

  // Distribute STT words proportionally
  const totalSTTWords = sttData.words.length;
  let currentSTTIndex = 0;

  return content.map((item, index) => {
    const wordCount = transcriptWordCounts[index];

    if (wordCount === 0 || !item.transcript) {
      return { ...item, sttSlice: null };
    }

    // Calculate how many STT words this content item should get
    const proportion = wordCount / totalTranscriptWords;
    const expectedSTTWords = Math.round(proportion * totalSTTWords);

    // Ensure we don't go beyond available STT words
    const actualSTTWords = Math.min(expectedSTTWords, totalSTTWords - currentSTTIndex);
    const endIndex = currentSTTIndex + actualSTTWords;

    const sttSlice = {
      displayWords: parseDisplayText(preprocessTranscriptForBrackets(item.transcript)),
      words: words.slice(currentSTTIndex, endIndex),
      startTimes: startTimes.slice(currentSTTIndex, endIndex),
      endTimes: endTimes.slice(currentSTTIndex, endIndex),
    };

    currentSTTIndex = endIndex;

    return { ...item, sttSlice };
  });
};
